import { createSlice } from '@reduxjs/toolkit'

// Helper function to filter out permissions from menu items
const filterPermissionsFromMenu = (menuItems) => {
    if (!Array.isArray(menuItems)) return []
    
    return menuItems
        .filter(item => {
            // Filter out items with type "Permissions" or isMenu === 0
            return item && item.type !== 'Permissions' && item.isMenu !== 0
        })
        .map(item => {
            // Recursively filter subMenu items
            if (item.subMenu && Array.isArray(item.subMenu)) {
                return {
                    ...item,
                    subMenu: filterPermissionsFromMenu(item.subMenu)
                }
            }
            return item
        })
}

// Helper function to extract permissions from menu items
const extractPermissionsFromMenu = (menuItems) => {
    if (!Array.isArray(menuItems)) return []
    
    const permissions = []
    
    menuItems.forEach(item => {
        if (item && (item.type === 'Permissions' || item.isMenu === 0)) {
            // Extract permission key/identifier
            permissions.push({
                key: item.key,
                path: item.path,
                title: item.title,
                authority: item.authority
            })
        }
        
        // Recursively extract from subMenu
        if (item.subMenu && Array.isArray(item.subMenu)) {
            permissions.push(...extractPermissionsFromMenu(item.subMenu))
        }
    })
    
    return permissions
}

/** Flatten menu tree so every path/authority from login `data` is available for route checks. */
const flattenMenuPathsForPermissions = (menuItems) => {
    if (!Array.isArray(menuItems)) return []
    const result = []
    const walk = (items) => {
        for (const item of items) {
            if (!item) continue
            result.push(item)
            if (item.subMenu && Array.isArray(item.subMenu)) {
                walk(item.subMenu)
            }
        }
    }
    walk(menuItems)
    return result
}

// Function to restore session from localStorage
const restoreSessionFromStorage = () => {
    try {
        const storedData = localStorage.getItem('LPA_Admin')
        if (storedData) {
            const parsed = JSON.parse(storedData)
            const allMenuItems = parsed.data || []
            
            // Filter out permissions from navigation
            const navigation = filterPermissionsFromMenu(allMenuItems)
            
            // Extract permissions from menu items
            const permissionsFromMenu = extractPermissionsFromMenu(allMenuItems)
            const flattenedFromData = flattenMenuPathsForPermissions(allMenuItems)
            
            // data2: flat list from API; data tree: ensure paths not missing from data2
            const allPermissions = [...(parsed.data2 || []), ...flattenedFromData, ...permissionsFromMenu]
            
            return {
                token: parsed.token || '',
                signedIn: !!parsed.token,
                permissions: allPermissions,
                navigation: navigation
            }
        }
    } catch (error) {
        console.error('Error restoring session from storage:', error)
    }
    return {
        token: '',
        signedIn: false,
        permissions: [],
        navigation: []
    }
}

export const sessionSlice = createSlice({
	name: 'auth/session',
	initialState: restoreSessionFromStorage(),
	reducers: {
        onSignInSuccess: (state, action) => {
            state.signedIn = true
            
            state.token = action.payload.token || ''
            
            // Filter out permissions from navigation menu items
            const allMenuItems = action.payload.data || []
            const newNavigation = filterPermissionsFromMenu(allMenuItems)
            
            // Extract permissions from menu items and combine with data2 + full menu tree paths
            const permissionsFromMenu = extractPermissionsFromMenu(allMenuItems)
            const flattenedFromData = flattenMenuPathsForPermissions(allMenuItems)
            const existingPermissions = action.payload.data2 || []
            const newPermissions = [...existingPermissions, ...flattenedFromData, ...permissionsFromMenu]
            
            // Only update navigation if it's different to prevent unnecessary re-renders
            if (JSON.stringify(state.navigation) !== JSON.stringify(newNavigation)) {
                state.navigation = newNavigation
            }
            
            // Only update permissions if it's different
            if (JSON.stringify(state.permissions) !== JSON.stringify(newPermissions)) {
                state.permissions = newPermissions
            }
        },
        onSignOutSuccess: (state) => {
            state.signedIn = false
            state.token = ''
            state.permissions = []
            state.navigation = []
        },
        setToken: (state, action) =>  {
            state.token = action.payload
        },
        clearSession: (state) => {
            state.signedIn = false
            state.token = ''
            state.permissions = []
            state.navigation = []
        }
	},
})

export const { onSignInSuccess, onSignOutSuccess, setToken, clearSession } = sessionSlice.actions

export default sessionSlice.reducer